import { strapi } from 'networking';
import { InferGetStaticPropsType } from 'next';
import Image from 'next/image';
import { SVGProps } from 'react';
import { shuffleArray } from 'shared';
import { StrapiCorporatePartnersResponse } from 'types';
import hero from '../assets/partners/hero.jpg';
import howItWorks from '../assets/partners/how-it-works-min.jpg';
import { Button } from '../components/general/button';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';
import { DynamicLink } from './agencies';

export async function getStaticProps() {
  const params = {
    populate: { logo: { fields: ['name', 'url', 'height', 'width'] } },
    pagination: { page: 1, pageSize: 100 },
  };

  const [data] = await strapi.get<StrapiCorporatePartnersResponse>(`/api/corporate-partners`, { params });
  const partners = shuffleArray(data.data.map(p => p.attributes));

  return {
    props: { partners },
    revalidate: 21600,
  };
}

export default function CorporatePartners({ partners }: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <main>
      <Meta title="corporate partners" description="Beds for Kids Corporate Partners" noDefaultFooter />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Corporate Volunteers"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="bg-white pb-12 text-blue-500">
        <h1 className="mx-auto my-4 max-w-3xl text-center font-operetta text-6xl">corporate partners</h1>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          We rely on the generosity and involvement of corporate partners. Whether it is a large group volunteering in
          our warehouse, a truckload of furniture from their distribution center or a monetary donation to fund a
          specific need, Beds for Kids is able to operate more efficiently with the help of these incredible
          organizations.
        </p>
        <div className="py-8 text-center">
          <Button link="#get-involved">how to help</Button>
        </div>
      </section>
      <section className="bg-blue-500 text-white">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-5">
          <div className="md:col-span-2">
            <Image
              src={howItWorks}
              alt="Stacked Pillows"
              className="aspect-[3/4] h-full object-cover object-center"
              sizes="100vw, (min-width: 768px) 33vw"
            />
          </div>
          <div className="flex h-full flex-col justify-center px-4 pb-12 pt-4 text-center md:col-span-3 md:pb-0 md:pt-0 md:text-left">
            <h2 className="mb-4 font-operetta text-5xl">how it works</h2>
            <p className="font-avenir text-lg leading-6">
              Is your company looking for its next volunteer opportunity or way to give back through charitable giving?
              Let&#39;s have a conversation! Beds for Kids looks at corporate partners from a holistic perspective and
              aligns your interests and talents to find the most rewarding experience for your group.
            </p>
          </div>
        </div>
      </section>
      <section className="bg-white pt-12 text-blue-500" id="get-involved">
        <h2 className="mx-auto mb-4 max-w-3xl text-center font-operetta text-6xl">want to get involved?</h2>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          Our most successful partnerships span a multi-year life cycle and involve a number of different tactics. Here
          are a handful of ways your brand can partner with Beds for Kids.
        </p>
        <div className="mx-auto grid max-w-4xl gap-12 px-12 py-12 md:grid-cols-3 md:px-0">
          <div className="flex flex-col items-center text-center">
            <GiveSVG />
            <h3 className="-mt-2 font-caliban text-6xl text-green-500">give</h3>
            <p className="mt-4 font-avenir text-lg leading-6">
              Contribute to projects, operations, or the tools that make our work possible.
            </p>
            <div className="mt-4 border-t-2 border-blue-500 px-24 pt-4" />
            <ul className="font-avenir text-lg leading-6 text-grey-500">
              <li>Support Operations</li>
              <li>Monetary Matching</li>
              <li>Corporate Gift</li>
              <li>Gifts in Kind</li>
            </ul>
          </div>
          <div className="flex flex-col items-center text-center">
            <EngageSVG />
            <h3 className="-mt-2 font-caliban text-6xl text-green-500">engage</h3>
            <p className="mt-4 font-avenir text-lg leading-6">
              Rally your employees and customers to fundraise and help spread the word.
            </p>
            <div className="mt-4 border-t-2 border-blue-500 px-24 pt-4" />
            <ul className="font-avenir text-lg leading-6 text-grey-500">
              <li>Community Fundraising</li>
              <li>Community Outreach</li>
            </ul>
          </div>
          <div className="flex flex-col items-center text-center">
            <AmplifySVG />
            <h3 className="-mt-2 font-caliban text-6xl text-green-500">amplify</h3>
            <p className="mt-4 font-avenir text-lg leading-6">
              Help shine a bigger spotlight on our mission and the impact of our work.
            </p>
            <div className="mt-4 border-t-2 border-blue-500 px-24 pt-4" />
            <ul className="font-avenir text-lg leading-6 text-grey-500">
              <li>Media Support &amp; Inventory</li>
              <li>Event Integration</li>
            </ul>
          </div>
        </div>
        <section>
          <h2 className="mx-auto mb-4 max-w-3xl text-center font-operetta text-6xl">
            current
            <br />
            corporate partners
          </h2>
          <div className="mx-auto grid max-w-5xl grid-cols-3 items-center justify-center gap-x-12 border-b-2 border-green-500 px-4 pb-8 md:grid-cols-6">
            {partners.map(p => (
              <div className="flex aspect-1 items-center justify-center" key={`${p.name}`}>
                <DynamicLink href={p.link}>
                  <Image
                    src={p.logo.data.attributes.url}
                    alt={p.name}
                    sizes="33vw, (min-width: 768px) 17vw"
                    height={p.logo.data.attributes.height}
                    width={p.logo.data.attributes.width}
                  />
                </DynamicLink>
              </div>
            ))}
          </div>
          <div className="mx-auto max-w-3xl px-4 pb-12 pt-8">
            <p className="text-center font-avenir text-xl leading-6">
              These corporate partners support us throughout the year in the warehouse volunteering, through grants,
              fundraisers, furniture donation and numerous bedding and welcome home basket donations.
            </p>
          </div>
          <BlueArch />
        </section>
      </section>
      <section className="bg-blue-500 pb-12 pt-4 text-center text-white">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="font-operetta text-5xl">become a partner</h2>
          <h6 className="mt-8 font-avenir text-2xl font-medium">Think you might be a good fit for Beds for Kids?</h6>
          <p className="mt-8 font-avenir text-lg leading-6">
            Head over to the contact page and tell us a little bit about your company! We are excited to hear from you!
          </p>
          <div className="mt-8 text-center">
            <Button link="/contact#form" bType="secondary-white">
              contact us now!
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}

// svgs fuuuuuuuuuu
const AmplifySVG = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 400" {...props}>
    <defs>
      <style>
        {'.amplify-icon-1{fill:url(#amplify-gradient);}.amplify-icon-2{fill:#fff;}.amplify-icon-3{fill:#0f004e;}'}
      </style>
      <linearGradient
        id="amplify-gradient"
        x1={256}
        y1={400.03}
        x2={256}
        y2={10.43}
        gradientTransform="matrix(1, 0, 0, 1, 0, 0)"
        gradientUnits="userSpaceOnUse">
        <stop offset={0} stopColor="#cee7ef" stopOpacity={0.1} />
        <stop offset={1} stopColor="#cee7ef" />
      </linearGradient>
    </defs>
    <path
      className="amplify-icon-1"
      d="M94.62,400.03C5.5,310.9,5.5,166.4,94.62,77.27c89.13-89.13,233.63-89.13,322.75,0,89.13,89.13,89.13,233.63,0,322.75H94.62Z"
    />
    <path
      className="amplify-icon-2"
      d="M155.62,295.29l-1.59,.6c-.28,.1-.54,.21-.8,.32-1.61,.65-3.49,1.11-5.31,1.59-4.7,1.26-9.41,2.51-14.12,3.77-13.3,3.54-27.06,7.21-40.54,10.96l-.13,.03c-3.13,.84-6.24,.51-8.75-.94-2.6-1.5-4.43-4.05-5.29-7.37-2.48-9.62-5.07-19.44-7.8-29.63-2.64-9.87-5.42-20.07-8.37-30.75-.91-3.3-.61-6.52,.85-9.07,1.41-2.47,3.84-4.23,7.02-5.08,19.8-5.32,39.61-10.62,59.42-15.91,.44-.12,.83-.15,1.25-.18,.18-.02,.38-.03,.6-.06l1.61-.19,21.95,81.91Z"
    />
    <g>
      <path
        className="amplify-icon-3"
        d="M352.92,172.59l-6-1.54c-2.85-.73-5.69-1.46-8.54-2.19-.77-.2-1.39-.35-1.91-.37-1.35-.03-1.64,.69-1.74,1.32-.09,.56-.02,1.2,1.16,1.69,.41,.17,.9,.29,1.43,.42l.63,.16c1.35,.35,2.71,.71,4.06,1.08,3.2,.85,6.51,1.74,9.77,2.49,5.05,1.17,8.12,4.2,9.39,9.24,1.87,7.43,3.8,14.63,5.74,21.4,1.42,4.95,.29,9.11-3.44,12.71-3.71,3.57-7.44,7.26-11.04,10.83-1.99,1.97-3.99,3.94-5.99,5.91-.43,.42-.89,.77-1.38,1.13-.23,.17-.48,.36-.75,.57l-2.24,1.77-36.88-137.65c-.23-.9-.45-1.74-.74-2.54-1.92-5.27-6.98-8.25-12.6-7.4-4.98,.75-8.98,5.42-9.11,10.63-.02,.91,.11,1.89,.26,2.93,.08,.54,.15,1.09,.21,1.64l.12,1.07-1.42,1.05c-.44,.33-.9,.68-1.38,1-10.81,7.36-21.61,14.72-32.42,22.07l-100.11,68.09c-.81,.54-1.58,1.05-2.17,1.64-.55,.55-.42,1.23-.12,1.69,.63,.95,1.17,.86,1.67,.69,.75-.26,1.57-.81,2.22-1.25,3.91-2.64,7.82-5.3,11.72-7.96l123.29-83.77,30.8,114.98c.21,.82,.41,1.6,.69,2.3,.34,.86,.9,1.22,1.7,1.08,.85-.12,1.33-.81,1.27-1.7-.05-.71-.27-1.49-.5-2.31l-.19-.67-31.39-117.19-.23-.84c-.24-.84-.48-1.71-.65-2.63-.72-3.98,1.29-7.56,5-8.9,3.99-1.44,7.9,.13,9.79,3.89,.66,1.32,1.03,2.7,1.39,4.04l.05,.19c18.14,67.67,36.27,135.34,54.4,203.01l.18,.66c.2,.69,.4,1.4,.52,2.16,.8,4.77-1.73,8.72-6.12,9.68-4.24,.9-8-1.53-9.34-6.05-.48-1.62-.91-3.26-1.34-4.9l-2.3-8.62c-1.39-5.19-2.78-10.37-4.18-15.56-.87-3.23-1.66-3.05-2.42-2.87-.46,.1-.77,.27-.89,.47-.13,.22-.38,.95,.17,3.04,.66,2.53,1.34,5.06,2.02,7.59l.89,3.29,1.83,6.82,1.69,6.31-5.09-.32c-1.62-.1-3.12-.19-4.62-.3l-152.32-11.25c-1.12-.09-2.18-.17-3.17-.08-.09,0-.18,.02-.26,.05-.77,.21-.94,.96-.97,1.43-.03,.54,.2,1.24,.97,1.47,.47,.14,1.07,.18,1.71,.22l.58,.04,158.85,11.78c.94,.07,1.87,.17,2.78,.27l2.37,.24,.65,1.98c.13,.4,.23,.73,.35,1.05,1.84,5.04,6.87,8.07,11.94,7.21,5.74-.99,9.59-5.32,9.58-10.82,0-1.39-.24-2.94-.76-4.88-4.1-15.44-8.24-30.87-12.38-46.3l-4.48-16.7,1.7-1.72c.59-.6,1.12-1.13,1.66-1.66,6.48-6.39,12.97-12.78,19.46-19.16,4.34-4.27,5.66-9.18,4.04-15.02-2.39-8.58-4.27-15.62-5.93-22.16-1.52-6-5.19-9.66-11.21-11.2Z"
      />
      <path
        className="amplify-icon-3"
        d="M179.48,389.42c2.15-4.18,2.44-8.81,.88-14.59-11.17-41.53-22.3-83.08-33.43-124.63l-10.19-38.05c-.13-.53-.26-1.02-.42-1.46-.41-1.06-.93-1.39-2-1.25-.62,.08-1.27,.26-1.97,.46l-.76,.21c-18.37,4.92-36.74,9.84-55.11,14.77-.76,.2-1.52,.4-2.28,.6-1.84,.47-3.73,.96-5.54,1.54-6.51,2.08-10.3,8.07-9.43,14.91,.08,.62,.55,3.45,.55,3.45l-1.81,.97c-.33,.21-.62,.39-.92,.54-9.98,5.35-14.64,15.82-11.88,26.68,1.42,5.59,2.66,10.18,3.89,14.46,2.8,9.73,11.54,16.43,21.75,16.67,.77,.02,1.54,.03,2.34,.03h2.59s.95,2.56,1.14,3.12c2.02,5.82,8.81,9.62,14.81,8.31l1.23-.26c1.16-.25,2.26-.48,3.35-.77,14.12-3.77,28.23-7.56,42.35-11.34l18.09-4.85,20.68,77.16c.18,.67,.36,1.33,.49,2.01,1.42,7.51-2.59,14.9-9.54,17.57-7.48,2.85-15.35,.17-19.17-6.56-1.04-1.83-1.58-3.84-2.11-5.78-3.68-13.66-7.32-27.24-10.96-40.82l-5.63-20.98-.11-.43c-.24-.9-.46-1.74-.81-2.44-.39-.77-.97-1.04-1.79-.82-.01,0-.02,0-.03,0-.71,.2-1.16,.55-1.07,1.64,.08,.9,.33,1.84,.6,2.83l.05,.18c5.47,20.46,10.95,40.92,16.45,61.37,.36,1.34,.8,2.7,1.3,4.06,2.67,7.16,9.28,11.9,16.82,12.08,7.96,.19,13.87-3.38,17.6-10.6Zm-104.91-88.11l-2.24,.05c-9.37,.35-17.43-5.16-20.06-13.72-1.67-5.43-3.1-10.79-4.27-15.96-2-8.84,2.52-18.15,10.51-21.66l2.18-1,13.88,52.3Zm81.05-6.02l-1.59,.6c-.28,.1-.54,.21-.8,.32-1.61,.65-3.49,1.11-5.31,1.59-4.7,1.26-9.41,2.51-14.12,3.77-13.3,3.54-27.06,7.21-40.54,10.96l-.13,.03c-3.13,.84-6.24,.51-8.75-.94-2.6-1.5-4.43-4.05-5.29-7.37-2.48-9.62-5.07-19.44-7.8-29.63-2.64-9.87-5.42-20.07-8.37-30.75-.91-3.3-.61-6.52,.85-9.07,1.41-2.47,3.84-4.23,7.02-5.08,19.8-5.32,39.61-10.62,59.42-15.91,.44-.12,.83-.15,1.25-.18,.18-.02,.38-.03,.6-.06l1.61-.19,21.95,81.91Z"
      />
      <path
        className="amplify-icon-3"
        d="M327.19,257.79c-.34-1.07-1.08-2.1-2.16-1.81-.02,0-.03,0-.05,.01-.92,.28-1.22,1.1-.89,2.44,.91,3.72,1.83,7.13,2.81,10.44,.5,1.7,1.27,1.54,1.83,1.43,.43-.08,1.56-.32,1.2-2.19-.22-1.15-.55-2.29-.89-3.49-.16-.55-.32-1.1-.46-1.65l-.46-1.82c-.3-1.16-.59-2.27-.93-3.37Z"
      />
      <path
        className="amplify-icon-3"
        d="M123.48,249.93c-11.19,2.97-22.38,5.98-33.56,8.98l-2.68,.72-.25,.06c-.52,.14-1.01,.27-1.4,.45-1.37,.63-1.33,1.33-1.18,1.94,.16,.64,.5,1.28,1.86,1.17,.71-.06,1.49-.27,2.3-.48l.37-.1c4.28-1.14,8.57-2.29,12.85-3.43l4.28-1.15,4.54-1.21c4.41-1.18,8.83-2.36,13.24-3.56,.95-.26,1.93-.56,2.72-1.02,.79-.45,.77-1.14,.62-1.63-.28-.91-.76-1.05-1.25-1.07-.69-.02-1.49,.09-2.46,.35Z"
      />
      <path
        className="amplify-icon-3"
        d="M119.21,234.39l-.43,.12c-4.29,1.14-8.57,2.29-12.85,3.44l-4.28,1.15-4.53,1.21c-4.53,1.21-9.06,2.43-13.59,3.65l-.44,.12c-.12,.03-.25,.06-.37,.1-.55,.15-1.08,.3-1.54,.5-1.02,.44-1.13,1-.99,1.7,.18,.86,.68,1.27,1.53,1.26,.61,0,1.32-.2,2.06-.4l.14-.04c12.08-3.22,24.16-6.46,36.24-9.71l.35-.09c.58-.16,1.13-.3,1.54-.51,.81-.42,1.18-1.23,.9-2.01-.19-.54-.56-1.08-1.78-.93-.62,.08-1.28,.26-1.98,.45Z"
      />
      <path
        className="amplify-icon-3"
        d="M128.4,265.44l-.31,.08c-12.41,3.32-24.82,6.64-37.23,9.98-.6,.16-1.12,.3-1.48,.51-.75,.42-1,1.05-.74,1.87,.26,.81,.76,1.11,1.67,1.01,.6-.07,1.25-.24,1.93-.43l.46-.12c5.09-1.36,10.18-2.72,15.28-4.09l2.18-.59,4.15-1.11c3.23-.86,6.45-1.72,9.68-2.6l1.6-.42c1.69-.44,3.28-.86,4.83-1.4,.83-.29,1.14-.81,1-1.64-.14-.81-.86-1.35-1.75-1.32-.35,.01-.8,.14-1.28,.27Z"
      />
      <path
        className="amplify-icon-3"
        d="M135.59,281.98c-.22-.78-.81-1.16-1.65-1.09-.47,.04-1,.19-1.55,.34l-.46,.13c-12.08,3.23-24.16,6.47-36.24,9.71l-.53,.14c-.55,.14-1.06,.27-1.48,.46-.8,.36-1.07,.9-.87,1.74,.14,.59,.52,1.28,1.52,1.21,.51-.03,1.1-.2,1.72-.37l.57-.16c4.53-1.21,9.06-2.42,13.59-3.64l4.53-1.22,4.85-1.3c4.31-1.16,8.62-2.31,12.94-3.47l.65-.16c.6-.15,1.18-.3,1.63-.51,.66-.32,.99-1.09,.79-1.82Z"
      />
      <path
        className="amplify-icon-3"
        d="M450.02,163.53l-53.62,14.37c-.8,.22-1.63-.26-1.85-1.07-.22-.8,.26-1.63,1.07-1.85l53.62-14.37c.8-.22,1.63,.26,1.85,1.07,.22,.8-.26,1.63-1.07,1.85Z"
      />
      <path
        className="amplify-icon-3"
        d="M466.49,246.45c-.25,.07-.52,.07-.78,0l-55.73-14.93c-.8-.22-1.28-1.04-1.07-1.85,.22-.81,1.05-1.28,1.85-1.07l55.73,14.93c.8,.22,1.28,1.04,1.07,1.85-.15,.54-.56,.93-1.07,1.07Z"
      />
      <path
        className="amplify-icon-3"
        d="M382.04,124.28c-.5,.13-1.06,0-1.46-.39-.59-.59-.59-1.54,0-2.13l40.79-40.79c.59-.59,1.54-.59,2.13,0,.59,.59,.59,1.54,0,2.13l-40.79,40.79c-.19,.19-.43,.32-.67,.39Z"
      />
    </g>
  </svg>
);

const GiveSVG = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 400" {...props}>
    <defs>
      <style>{'.give-icon-1{fill:url(#give-gradient);}.give-icon-2{fill:#fff;}.give-icon-3{fill:#0f004e;}'}</style>
      <linearGradient
        id="give-gradient"
        x1={256}
        y1={400.03}
        x2={256}
        y2={10.43}
        gradientTransform="matrix(1, 0, 0, 1, 0, 0)"
        gradientUnits="userSpaceOnUse">
        <stop offset={0} stopColor="#cee7ef" stopOpacity={0.1} />
        <stop offset={1} stopColor="#cee7ef" />
      </linearGradient>
    </defs>
    <path
      className="give-icon-1"
      d="M94.62,400.03C5.5,310.9,5.5,166.4,94.62,77.27c89.13-89.13,233.63-89.13,322.75,0,89.13,89.13,89.13,233.63,0,322.75H94.62Z"
    />
    <g>
      <path
        className="give-icon-2"
        d="M183.68,354.98c.37,.56,.7,1.15,1,1.75,2.38,4.76,.93,10.51-3.37,13.41l-.13,.08c-4.41,2.91-8.84,5.82-13.27,8.74l-8.73,5.75c-4.49,2.96-8.99,5.93-13.51,8.86-2.54,1.65-4.86,2.48-6.99,2.48-3.24,0-6.05-1.91-8.6-5.78-7.61-11.54-15.21-23.08-22.82-34.62-8.59-13.05-17.18-26.1-25.77-39.15-4.36-6.63-3.37-11.53,3.2-15.88,5.73-3.79,11.46-7.57,17.2-11.34,5.79-3.81,11.59-7.62,17.38-11.43,6.6-4.33,11.49-3.3,15.86,3.32,16.2,24.59,32.39,49.18,48.54,73.81Z"
      />
      <g>
        <path
          className="give-icon-3"
          d="M264.62,351.54c.57,.1,1.21,.09,1.9,.09h7.84c6.27,0,12.52,0,18.78-.05,25.47-.22,49.12-6.93,70.31-19.95,21.07-12.95,37.17-29.76,47.84-49.96,4.58-8.66,9.2-17.29,13.82-25.93,2.29-4.28,4.59-8.57,6.88-12.86,.42-.78,.84-1.55,1.27-2.33,1.03-1.87,2.09-3.8,2.96-5.74,1.18-2.62,1.22-5.42,.13-7.87-1.12-2.52-3.31-4.42-6.15-5.34-13.63-4.39-28.02,.08-36.66,11.4-1.6,2.1-3.05,4.41-4.45,6.66l-.76,1.21c-6.46,10.26-13.6,17.89-21.84,23.33-9.04,5.97-19.06,9.27-29.78,9.81l-1.15,.06-.55-1.01c-4.13-7.61-10.79-10.98-20.99-10.66-9.39,.31-18.93,.17-28.16,.04l-1.15-.02c-3.8-.05-7.84-.39-12.01-1.01-7.65-1.13-14.37-4.66-20.86-8.07l-1.25-.65c-5.67-2.97-13.25-6.44-21.62-6.95-7.63-.46-15.53-.59-23.49-.38-15.39,.4-29.11,6.14-40.79,17.05-5.16,4.82-10.35,9.6-15.54,14.37l-3.21,2.96c-.61,.56-1.26,1.09-1.93,1.65l-2.49,2.08-2.13-3.16c-.66-.99-1.26-1.88-1.88-2.76-2.93-4.17-7-6.35-11.28-6.35-2.56,0-5.2,.78-7.7,2.4-12.19,7.86-24.75,16.13-37.33,24.57-6.72,4.51-8.3,12.41-3.84,19.22,15.94,24.35,32.76,49.9,49.99,75.94,4.7,7.09,12.45,8.68,19.75,4.05,2.43-1.55,4.83-3.15,7.23-4.74,1.18-.79,2.36-1.58,3.55-2.36,2.66-1.76,5.33-3.5,8-5.25,5.88-3.86,11.97-7.85,17.9-11.84,5.73-3.86,7.74-9.81,5.5-16.33-.31-.9-.71-1.71-1.24-2.5l-1.89-2.84h3.41c1.75,0,3.54,.02,5.28,.05,1.63,.02,3.22,.04,4.69,.04,3.2,0,5.1-.28,5.64-.84,.07-.07,.27-.27,.24-1.16-.03-1.11-.05-1.91-5.71-1.91h-16l-39.51-60.03,.66-1c.11-.16,.19-.3,.26-.43,.19-.33,.41-.7,.77-1.03,2.38-2.19,4.74-4.39,7.11-6.59,5.76-5.36,11.72-10.9,17.75-16.16,8.16-7.13,17.33-11.26,27.24-12.29,6.63-.69,13.4-.73,19.94-.77,1.9-.01,3.79-.02,5.68-.05,6.08-.05,12.12,1.15,18,3.66,4.65,1.99,9.09,4.22,12.44,5.94,8.55,4.4,15.89,6.66,23.12,7.12,4.46,.28,9.04,.29,13.48,.29,1.26,0,2.53,0,3.79,0,3.1,.02,6.19,0,9.29,0,4.84-.01,9.84-.03,14.78,.07,6.97,.14,13.04,5.42,14.13,12.27,1.2,7.58-2.82,14.42-9.77,16.65-2.33,.75-4.9,.85-7.11,.86-13.15,.04-26.3,.04-39.46,.04h-19.73c-1.37,0-2.59,.02-3.74,.22-.69,.12-1.49,.43-1.5,1.86,0,.58,.14,1.39,1.42,1.78,.55,.17,1.26,.19,2.05,.21,1.58,.02,3.16,.02,4.74,.02h1.89c5.49,0,10.98,0,16.48,0,12.91,.02,26.25,.03,39.37-.04,10.67-.06,18.36-7.01,19.59-17.7l.48-4.21,2.06-.45c.6-.14,1.14-.27,1.69-.34,7.23-.87,14.24-3.01,20.84-6.34,11.95-6.04,21.83-15.46,30.2-28.8,.36-.58,.72-1.16,1.08-1.74,1.26-2.06,2.57-4.19,4.14-6.13,7.45-9.24,19.14-12.73,31.26-9.32,2.5,.7,3.58,2.07,4.05,3.1,.71,1.56,.58,3.41-.4,5.48-.33,.7-.7,1.37-1.08,2.04-.18,.32-.36,.64-.53,.97-2.25,4.21-4.51,8.42-6.78,12.63-5,9.29-10.17,18.9-15.08,28.43-10.76,20.88-27.07,38.02-48.47,50.94-20.48,12.37-43.65,18.7-68.87,18.84-5.06,.03-10.12,.02-15.19,.02-3.08,0-6.16,0-9.23,0h-.67c-.69-.02-1.33-.04-1.83,.09-1.11,.28-1.54,.83-1.5,1.91,.04,1.11,.48,1.62,1.59,1.8Zm-80.93,3.44c.37,.56,.7,1.15,1,1.75,2.38,4.76,.93,10.51-3.37,13.41l-.13,.08c-4.41,2.91-8.84,5.82-13.27,8.74l-8.73,5.75c-4.49,2.96-8.99,5.93-13.51,8.86-2.54,1.65-4.86,2.48-6.99,2.48-3.24,0-6.05-1.91-8.6-5.78-7.61-11.54-15.21-23.08-22.82-34.62-8.59-13.05-17.18-26.1-25.77-39.15-4.36-6.63-3.37-11.53,3.2-15.88,5.73-3.79,11.46-7.57,17.2-11.34,5.79-3.81,11.59-7.62,17.38-11.43,6.6-4.33,11.49-3.3,15.86,3.32,16.2,24.59,32.39,49.18,48.54,73.81Z"
        />
        <path
          className="give-icon-3"
          d="M233.53,349.69c0-.44,0-1.78-2.05-1.91-1.28-.09-2.58-.07-3.94-.04h-.02v.03h-1.83c-.72,0-1.45-.01-2.17-.02-.72-.01-1.43-.02-2.12-.02-.88,0-1.74,.02-2.6,.08-1.27,.09-1.89,.76-1.84,1.99,.07,1.52,1.05,1.73,1.78,1.76,4.21,.14,8.53,.15,12.82,.02,1.35-.04,1.97-.63,1.97-1.87Z"
        />
        <g>
          <path
            className="give-icon-3"
            d="M182.29,160.87c3.57,2.13,6.79,4.04,10.11,5.81,1.15,.61,2.23,.12,2.68-.65,.48-.84,.41-2.04-.66-2.72-2.7-1.7-5.55-3.31-8.31-4.86l-1.78-1c-.22-.07-.44-.11-.65-.11-.46,0-.89,.17-1.25,.46-.3,.25-.73,.63-.92,1-.53,1.07,.26,1.75,.78,2.06Z"
          />
          <path
            className="give-icon-3"
            d="M436.16,157.71c-.24-.15-.53-.23-.84-.23-.36,0-.76,.11-1.17,.31-2.86,1.45-5.87,3.18-9.75,5.59-1.16,.73-1.51,1.86-.88,2.9,.6,.99,1.6,1.21,2.74,.6,3.62-1.9,6.8-3.74,9.73-5.62,.38-.25,.77-.88,1.04-1.34,.03-.05,.06-.1,.09-.14-.07-.97-.41-1.71-.97-2.07Z"
          />
          <path
            className="give-icon-3"
            d="M244.36,115.6c-2.16-4.14-4.29-7.85-6.51-11.33-.2-.31-.48-.52-.8-.58-.3-.07-.62,0-.92,.2-.89,.59-1.16,1.85-.65,2.88,1.55,3.11,4.36,8.19,6.19,10.99,.03,.05,.07,.1,.23,.13,.32,.06,.96-.06,1.57-.48,.5-.35,1.29-1.05,.89-1.8Z"
          />
          <path
            className="give-icon-3"
            d="M383.56,107.5c.4-.88,.23-1.85-.4-2.58h0c-.28-.33-.52-.54-.74-.65-.23-.12-.44-.17-.64-.17-.71,0-1.21,.66-1.41,1-1.82,2.99-3.93,6.53-5.83,10.11-.61,1.15-.38,2.03,.7,2.69,.4,.24,1.63,1,2.84-.92,1.67-2.66,3.25-5.46,4.77-8.18l.73-1.3Z"
          />
          <path
            className="give-icon-3"
            d="M307.37,97.65c.09,1.27,.82,2.07,1.91,2.08h.02c1.09,0,1.87-.8,1.91-1.96,.14-3.88,.14-7.8,0-11.64-.05-1.31-.65-1.93-1.9-1.94h-.02c-1.23,0-1.82,.6-1.92,1.95-.09,1.26-.07,2.54-.05,3.89,.01,.62,.02,1.24,.02,1.86s-.01,1.23-.02,1.84c-.02,1.36-.04,2.65,.04,3.92Z"
          />
          <path
            className="give-icon-3"
            d="M174.83,234.06c.16,0,.32,0,.47-.03,1.83-.26,2.07-1.12,2.06-2-.02-1.25-.68-1.91-1.96-1.95-1.91-.07-3.83-.11-5.75-.11s-3.93,.04-5.88,.12c-1.24,.05-1.96,1.06-1.95,2,.01,1.09,.81,1.81,2.12,1.91,1.27,.1,2.55,.07,3.91,.05,.61-.01,1.22-.02,1.84-.02l5.15,.04Z"
          />
          <path
            className="give-icon-3"
            d="M414.61,208.62c-.08-.71-.28-1.46-.5-2.25l-.19-.72c-12.47-47.3-55.85-80.2-105.63-80.2h-1.11c-12.49,0-26,2.81-39.15,8.13-39.25,15.89-66.19,55.39-67.05,98.3-.01,.58,.21,1.12,.61,1.54,.41,.42,.95,.64,1.53,.64,1.16,0,2.09-.91,2.12-2.07,.16-5.5,.73-11.17,1.7-16.86,4.62-27.16,20.46-51.79,43.46-67.59,23.14-15.89,51.03-21.59,78.53-16.05,41.62,8.38,68.96,33.99,81.25,76.1l.19,.67c.15,.55,.29,1.07,.49,1.48,.49,.98,1.16,1.29,2.3,1.06,.9-.19,1.57-1.18,1.46-2.17Z"
          />
        </g>
      </g>
    </g>
  </svg>
);

const EngageSVG = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 400" {...props}>
    <defs>
      <style>{'.engage-icon-1{fill:url(#engage-gradient);}.engage-icon-2{fill:#0f004e;}'}</style>
      <linearGradient
        id="engage-gradient"
        x1={256}
        y1={400.03}
        x2={256}
        y2={10.43}
        gradientTransform="matrix(1, 0, 0, 1, 0, 0)"
        gradientUnits="userSpaceOnUse">
        <stop offset={0} stopColor="#cee7ef" stopOpacity={0.1} />
        <stop offset={1} stopColor="#cee7ef" />
      </linearGradient>
    </defs>
    <path
      className="engage-icon-1"
      d="M94.62,400.03C5.5,310.9,5.5,166.4,94.62,77.27c89.13-89.13,233.63-89.13,322.75,0,89.13,89.13,89.13,233.63,0,322.75H94.62Z"
    />
    <g>
      <path
        className="engage-icon-2"
        d="M55.31,80.83c-2.55-.13-5.09-.1-7.78-.07l-1.99,.02c-24.85,.2-43.91,18.91-44.33,43.51-.37,21.8-.19,43.41-.01,64.31,.08,9.33,2.92,17.8,8.46,25.18,9.18,12.24,21.53,18.2,37.76,18.2h.02c20.49,0,40.98,0,61.46,0,14.38,0,28.76,0,43.14,0h43.07c4.93,0,9.85,0,14.78,.02,11.34,.02,23.07,.04,34.6-.05,13.56-.11,24.45-4.83,33.29-14.45,6.35-6.91,10.09-15.41,11.43-25.98l.28-2.16,7.09-2.97c3.79-1.59,7.55-3.17,11.33-4.72,18.29-7.5,36.59-14.98,54.89-22.44l.31-.13c3.15-1.28,3.48-1.66,3.51-2.73,.03-1.1-.11-1.48-3.49-2.87l-.52-.21c-3.22-1.33-6.45-2.65-9.68-3.96l-63.29-25.87-.3-2.16c-3.99-29.08-27.06-41.46-48.25-40.74-20.82,.67-41.97,.51-62.42,.36-8.96-.07-17.91-.13-26.87-.13h-1.48c-1.18-.02-2.3-.04-3.32,.07-1.18,.12-1.51,.47-1.64,1.67-.1,.93,.49,1.66,1.52,1.84,.89,.15,1.97,.14,3.11,.12,2.23-.04,4.43-.01,6.64-.01,8.27,0,16.54,0,24.81,0,21.02-.01,42.03-.02,63.05,.04,22.5,.07,39.26,15.7,40.75,38.02,.18,2.62,.92,3.66,3.3,4.61,15.3,6.16,30.81,12.53,45.8,18.69l11.16,4.59c1.13,.46,2.24,.98,3.55,1.6,.63,.3,1.32,.62,2.08,.97l7.87,3.62-8.01,3.3c-5.65,2.32-11.22,4.64-16.74,6.94-14.09,5.87-27.4,11.41-41.1,16.69-7.03,2.71-8.79,4.79-8.2,9.68,.12,1.04-.13,1.86-.27,2.3-4.12,20.05-19.92,32.54-41.26,32.58-52.41,.1-105.69,.08-157.22,.05l-39.3-.02c-7.46,0-13.3-1.17-18.4-3.68-15.54-7.64-23.46-20.47-23.53-38.11-.07-16.45-.04-33.17-.02-49.34l.02-10.65c0-1.8,.13-3.69,.38-5.96,2.37-20.8,18.7-35.57,39.72-35.91,1.34-.02,2.67-.01,4.01,0,2.21,.02,4.29,.04,6.36-.09,1.31-.08,2.15-.8,2.14-1.83-.02-1.46-1.58-1.69-2.25-1.73Z"
      />
      <path
        className="engage-icon-2"
        d="M99.15,82.46c-.04-.91-.19-1.51-1.8-1.61-2.02-.13-4.06-.1-6.22-.06-.98,.01-1.96,.03-2.93,.03l-3.77-.05c-.67,0-1.34-.01-1.99-.01-1.16,0-2.31,.02-3.45,.07-1.04,.05-2.29,.38-2.36,1.7-.02,.46,.15,.92,.47,1.26,.23,.25,.65,.56,1.34,.58,6.5,.22,12.92,.21,19.08-.01,.41-.02,.96-.1,1.27-.44,.27-.29,.4-.82,.37-1.45Z"
      />
      <path
        className="engage-icon-2"
        d="M120.11,150.62c-.07,0-.14,0-.21,0-1.53,.07-1.59,.7-1.64,1.31-.27,2.97-.23,6.04,.12,9.14,.04,.32,.1,.92,1.46,.99,1.1,.06,1.54-.28,1.8-1.38,.23-.93,.29-2.04,.35-3.02,.02-.38,.04-.74,.07-1.07-.03-.61-.05-1.17-.07-1.71-.03-1.02-.06-1.83-.18-2.48-.31-1.56-.99-1.78-1.7-1.78Z"
      />
      <path
        className="engage-icon-2"
        d="M172.24,154.71c.03-.88,.06-1.71-.03-2.37-.2-1.41-.8-1.7-1.77-1.7h-.06c-1.2,.01-1.59,.39-1.69,1.63-.23,2.79-.22,5.54,.02,8.19,.11,1.2,.97,1.63,1.69,1.63h.03c.96-.01,1.65-.75,1.8-1.93,0-.05,.01-.11,.02-.16h-.03v-3.77c0-.5,.02-1.01,.04-1.52Z"
      />
      <path
        className="engage-icon-2"
        d="M69.51,162.09c.32,.03,1.61,0,1.76-2.16,.18-2.84,.17-5.36-.04-7.7-.11-1.22-.51-1.59-1.72-1.59-1,0-1.62,.29-1.82,1.76-.09,.67-.06,1.5-.03,2.38,.02,.51,.03,1.01,.03,1.52v3.76c.12,.7,.52,2.02,1.82,2.03Z"
      />
      <path
        className="engage-icon-2"
        d="M221.07,150.66c-1.31-.11-1.65,.28-1.93,1.25-.07,.23-.12,.49-.16,.77v3.08c0,.56-.02,1.13-.04,1.7-.04,.99-.08,1.93,.04,2.69,.18,1.17,.93,1.95,1.86,1.95h0c.99,0,1.58-.61,1.68-1.71,.24-2.77,.21-5.68-.08-8.67-.05-.52-.09-.97-1.37-1.07Z"
      />
    </g>
    <g>
      <path
        className="engage-icon-2"
        d="M456.69,399.68c2.55,.13,5.09,.1,7.78,.07l1.99-.02c24.85-.2,43.91-18.91,44.33-43.51,.37-21.8,.19-43.41,.01-64.31-.08-9.33-2.92-17.8-8.46-25.18-9.18-12.24-21.53-18.2-37.76-18.2h-.02c-20.49,0-40.98,0-61.46,0-14.38,0-28.76,0-43.14,0h-43.07c-4.93,0-9.85,0-14.78-.02-11.34-.02-23.07-.04-34.6,.05-13.56,.11-24.45,4.83-33.29,14.45-6.35,6.91-10.09,15.41-11.43,25.98l-.28,2.16-7.09,2.97c-3.79,1.59-7.55,3.17-11.33,4.72-18.29,7.5-36.59,14.98-54.89,22.44l-.31,.13c-3.15,1.28-3.48,1.66-3.51,2.73-.03,1.1,.11,1.48,3.49,2.87l.52,.21c3.22,1.33,6.45,2.65,9.68,3.96l63.29,25.87,.3,2.16c3.99,29.08,27.06,41.46,48.25,40.74,20.82-.67,41.97-.51,62.42-.36,8.96,.07,17.91,.13,26.87,.13h1.48c1.18,.02,2.3,.04,3.32-.07,1.18-.12,1.51-.47,1.64-1.67,.1-.93-.49-1.66-1.52-1.84-.89-.15-1.97-.14-3.11-.12-2.23,.04-4.43,.01-6.64,.01-8.27,0-16.54,0-24.81,0-21.02,.01-42.03,.02-63.05-.04-22.5-.07-39.26-15.7-40.75-38.02-.18-2.62-.92-3.66-3.3-4.61-15.3-6.16-30.81-12.53-45.8-18.69l-11.16-4.59c-1.13-.46-2.24-.98-3.55-1.6-.63-.3-1.32-.62-2.08-.97l-7.87-3.62,8.01-3.3c5.65-2.32,11.22-4.64,16.74-6.94,14.09-5.87,27.4-11.41,41.1-16.69,7.03-2.71,8.79-4.79,8.2-9.68-.12-1.04,.13-1.86,.27-2.3,4.12-20.05,19.92-32.54,41.26-32.58,52.41-.1,105.69-.08,157.22-.05l39.3,.02c7.46,0,13.3,1.17,18.4,3.68,15.54,7.64,23.46,20.47,23.53,38.11,.07,16.45,.04,33.17,.02,49.34l-.02,10.65c0,1.8-.13,3.69-.38,5.96-2.37,20.8-18.7,35.57-39.72,35.91-1.34,.02-2.67,.01-4.01,0-2.21-.02-4.29-.04-6.36,.09-1.31,.08-2.15,.8-2.14,1.83,.02,1.46,1.58,1.69,2.25,1.73Z"
      />
      <path
        className="engage-icon-2"
        d="M412.85,398.05c.04,.91,.19,1.51,1.8,1.61,2.02,.13,4.06,.1,6.22,.06,.98-.01,1.96-.03,2.93-.03l3.77,.05c.67,0,1.34,.01,1.99,.01,1.16,0,2.31-.02,3.45-.07,1.04-.05,2.29-.38,2.36-1.7,.02-.46-.15-.92-.47-1.26-.23-.25-.65-.56-1.34-.58-6.5-.22-12.92-.21-19.08,.01-.41,.02-.96,.1-1.27,.44-.27,.29-.4,.82-.37,1.45Z"
      />
      <path
        className="engage-icon-2"
        d="M391.89,329.89c.07,0,.14,0,.21,0,1.53-.07,1.59-.7,1.64-1.31,.27-2.97,.23-6.04-.12-9.14-.04-.32-.1-.92-1.46-.99-1.1-.06-1.54,.28-1.8,1.38-.23,.93-.29,2.04-.35,3.02-.02,.38-.04,.74-.07,1.07,.03,.61,.05,1.17,.07,1.71,.03,1.02,.06,1.83,.18,2.48,.31,1.56,.99,1.78,1.7,1.78Z"
      />
      <path
        className="engage-icon-2"
        d="M339.76,325.8c-.03,.88-.06,1.71,.03,2.37,.2,1.41,.8,1.7,1.77,1.7h.06c1.2-.01,1.59-.39,1.69-1.63,.23-2.79,.22-5.54-.02-8.19-.11-1.2-.97-1.63-1.69-1.63h-.03c-.96,.01-1.65,.75-1.8,1.93,0,.05-.01,.11-.02,.16h.03v3.77c0,.5-.02,1.01-.04,1.52Z"
      />
      <path
        className="engage-icon-2"
        d="M442.49,318.42c-.32-.03-1.61,0-1.76,2.16-.18,2.84-.17,5.36,.04,7.7,.11,1.22,.51,1.59,1.72,1.59,1,0,1.62-.29,1.82-1.76,.09-.67,.06-1.5,.03-2.38-.02-.51-.03-1.01-.03-1.52v-3.76c-.12-.7-.52-2.02-1.82-2.03Z"
      />
      <path
        className="engage-icon-2"
        d="M290.93,329.85c1.31,.11,1.65-.28,1.93-1.25,.07-.23,.12-.49,.16-.77v-3.08c0-.56,.02-1.13,.04-1.7,.04-.99,.08-1.93-.04-2.69-.18-1.17-.93-1.95-1.86-1.95h0c-.99,0-1.58,.61-1.68,1.71-.24,2.77-.21,5.68,.08,8.67,.05,.52,.09,.97,1.37,1.07Z"
      />
    </g>
  </svg>
);
